// Studio Row's pieces: the banner built from a creator's games' art (type when there's no art), the lit-window card
// the directory and Home's "creators you follow" row use, and the spotlight card. No letter-avatar slabs anywhere:
// a creator is shown by what they made, or by their name set in type.

import type { Game } from "../../catalogue.ts";
import { lazyCover } from "../../components.ts";
import { h, link } from "../../dom.ts";
import { coverUrl } from "../../rows.ts";
import { bannerTiles, displayName, hasRealArt, madeLine, storyLine, type Studio } from "./data.ts";
import { followButton } from "./follow.ts";

const SAFE = /^#[0-9a-fA-F]{3,8}$/;
const colour = (c: string, d: string) => (SAFE.test(c) ? c : d);

/** A tile's background: the art over its palette (eager: the banner is above the fold). */
function tileStyle(g: Game, variant: "full" | "thumb"): Partial<CSSStyleDeclaration> {
  const art = coverUrl(g, variant);
  const grad = `linear-gradient(160deg, ${colour(g.palette.top, "#183038")}, ${colour(g.palette.bottom, "#0b1316")})`;
  return { backgroundImage: art ? `url("${art}"), ${grad}` : grad };
}

/**
 * The banner: a mosaic of up to five of their games' art (one big, the rest stacked), with a teal scrim so the name
 * reads. With no art: their name in type, large, over the zone's night street colours.
 */
export function studioBanner(name: string, games: readonly Game[], opts: { size?: "page" | "spot" } = {}): HTMLElement {
  const tiles = bannerTiles([...games]);
  const size = opts.size ?? "page";
  if (!tiles.length) {
    return h(
      "div",
      { class: `cr-banner cr-banner-type ${size}`, "aria-hidden": "true" },
      h("span", { class: "cr-banner-word" }, displayName({ name })),
      h("span", { class: "cr-banner-word ghost" }, displayName({ name })),
      h("span", { class: "cr-scrim" }),
    );
  }
  return h(
    "div",
    { class: `cr-banner ${size}`, "data-tiles": String(tiles.length), "aria-hidden": "true" },
    tiles.map((g, i) => h("span", { class: "cr-tile", style: tileStyle(g, i === 0 ? "full" : "thumb") })),
    h("span", { class: "cr-scrim" }),
  );
}

/** A lit studio window: their best art, their name, what they make. Opens their page. */
export function studioCard(s: Studio): HTMLElement {
  const lead = s.games[0];
  const name = displayName(s.creator);
  return h(
    "li",
    { class: "cr-card" },
    link(
      `/c/${s.creator.id}`,
      { class: "cr-card-link", "aria-label": `${name}: ${madeLine(s.games)}` },
      lead && coverUrl(lead) ? h("span", { class: hasRealArt(lead) ? "cr-window" : "cr-window typed", ...lazyCover(lead) }) : h("span", { class: "cr-window cr-window-type", "aria-hidden": "true" }, name),
      // a host isn't a studio name: their game leads, the site sits under it, small
      s.who === "host" && lead
        ? [h("span", { class: "cr-card-name" }, lead.title), h("span", { class: "cr-card-sub cr-card-host" }, s.games.length > 1 ? `${name} · ${s.games.length} games` : name)]
        : [
            h("span", { class: "cr-card-name" }, name),
            h("span", { class: "cr-card-sub" }, lead ? (s.games.length > 1 ? `${lead.title} and ${s.games.length - 1} more` : lead.title) : madeLine(s.games)),
          ],
    ),
  );
}

/** A spotlight: a wide banner, their name, one line made from their games, Follow and a way in. */
export function spotlightCard(s: Studio, story?: string): HTMLElement {
  const name = displayName(s.creator);
  return h(
    "li",
    { class: "cr-spot" },
    link(`/c/${s.creator.id}`, { class: "cr-spot-art", tabindex: "-1", "aria-hidden": "true" }, studioBanner(s.creator.name, s.games.slice(0, 1), { size: "spot" })),
    h(
      "div",
      { class: "cr-spot-text" },
      h("p", { class: "cr-eyebrow" }, madeLine(s.games)),
      h("h3", { class: "cr-spot-name" }, link(`/c/${s.creator.id}`, null, name)),
      h("p", { class: "cr-spot-story" }, story ?? storyLine(s)),
      h("div", { class: "cr-spot-actions" }, followButton(s.creator.id, name, { small: true }), link(`/c/${s.creator.id}`, { class: "cr-visit" }, "Visit the studio")),
    ),
  );
}
